import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth, can } from "@/auth";
import { useCountry } from "@/country";
import { PageHeader, Tile, Loading, OperatorLink } from "@/components/Bits";
import { Star, Plus, PencilSimple, Trash, UploadSimple, DownloadSimple, FileXls, X, CheckCircle, WarningCircle, ArrowRight } from "@phosphor-icons/react";

const EMPTY = { first_name: "", last_name: "", title: "", operator: "", city: "", department: "", email: "", phone: "", mobile: "", language: "TR/EN", decision_maker: false, linked_platforms: "", notes: "", relationship_score: 3 };

export default function Contacts() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { code: country } = useCountry();
  const editable = can(user, "sales");
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [importPreview, setImportPreview] = useState(null); // { summary, file }
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const L = (fr, en) => (lang === "fr" ? fr : en);

  const load = () => api.get("/contacts").then((r) => setRows(r.data.rows));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r }); setEditing(r); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    const body = { ...form, relationship_score: parseInt(form.relationship_score) || 3 };
    if (editing && editing.id) {
      await api.put(`/contacts/${editing.id}`, body);
    } else {
      await api.post("/contacts", body);
    }
    close(); await load();
  };
  const del = async (id) => {
    if (!window.confirm(L("Supprimer ce contact ?", "Delete this contact?"))) return;
    await api.delete(`/contacts/${id}`); await load();
  };

  // --- Import / Export -------------------------------------------------------
  const downloadFile = (path, suggested) => {
    const t0 = document.createElement("a");
    t0.href = `${API}${path}`;
    t0.target = "_blank";
    t0.rel = "noopener";
    t0.download = suggested || "";
    document.body.appendChild(t0);
    t0.click();
    t0.remove();
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!f) return;
    runDryRun(f);
  };

  const runDryRun = async (f) => {
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/contacts/import?dry_run=true`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportPreview({ summary: r.data, file: f });
    } catch (e) {
      toast.error(e.response?.data?.detail || L("Échec de la lecture du fichier", "Failed to read file"));
    } finally {
      setImporting(false);
    }
  };

  const confirmImport = async () => {
    if (!importPreview?.file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importPreview.file);
      const r = await api.post(`/contacts/import?dry_run=false`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        L(
          `${r.data.to_create} créé(s) · ${r.data.to_update} mis à jour`,
          `${r.data.to_create} created · ${r.data.to_update} updated`,
        ),
      );
      setImportPreview(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || L("Échec de l'import", "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  if (!rows) return <Loading />;

  const opFilter = new URLSearchParams(window.location.search).get("operator");
  const visibleRows = opFilter
    ? rows.filter((r) => (r.operator || "").trim().toLowerCase() === opFilter.trim().toLowerCase())
    : rows;

  return (
    <div data-testid="contacts-page">
      <PageHeader
        title={t("contacts.title")}
        subtitle={`${rows.length} contacts · ${country}`}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => downloadFile(`/contacts/export?country=${country}`)}
              data-testid="contacts-export-btn"
              className="btn-ghost flex items-center gap-1.5"
              title={L("Exporter en Excel", "Export to Excel")}
            >
              <DownloadSimple size={13} weight="bold" />
              <span className="hidden sm:inline">{L("Exporter", "Export")}</span>
            </button>
            {editable && (
              <>
                <button
                  onClick={() => downloadFile(`/contacts/template`)}
                  data-testid="contacts-template-btn"
                  className="btn-ghost flex items-center gap-1.5"
                  title={L("Télécharger un modèle vide", "Download a blank template")}
                >
                  <FileXls size={13} weight="bold" />
                  <span className="hidden sm:inline">{L("Modèle", "Template")}</span>
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xlsm,.csv"
                  className="hidden"
                  onChange={onPickFile}
                  data-testid="contacts-import-input"
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={importing}
                  data-testid="contacts-import-btn"
                  className="btn-ghost flex items-center gap-1.5"
                  title={L("Importer depuis un fichier Excel/CSV", "Import from Excel/CSV")}
                >
                  <UploadSimple size={13} weight="bold" />
                  <span className="hidden sm:inline">{L("Importer", "Import")}</span>
                </button>
                <button onClick={openNew} className="btn-accent flex items-center gap-2" data-testid="contact-new">
                  <Plus size={14} weight="bold" /> {t("contacts.newBtn")}
                </button>
              </>
            )}
          </div>
        }
      />
      <div className="p-4 md:p-8">
        {opFilter && (
          <div className="mb-3 flex items-center gap-2" data-testid="contacts-operator-filter">
            <span className="pill flex items-center gap-1.5" style={{ background: "rgba(13,113,221,0.10)", color: "#0D71DD", border: "1px solid rgba(13,113,221,0.35)" }}>
              {L("Opérateur :", "Operator:")} {opFilter}
              <button onClick={() => nav("/contacts")} className="font-bold hover:opacity-70" data-testid="contacts-filter-clear" aria-label="Clear filter">×</button>
            </span>
            <span className="text-[11.5px] font-mono" style={{ color: "var(--text-tertiary)" }}>
              {visibleRows.length} {L("contact(s)", "contact(s)")}
            </span>
          </div>
        )}
        <Tile>
          <div className="overflow-x-auto">
          <table className="tech contacts-table" data-testid="contacts-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Title</th>
                <th>{t("table.operator")}</th>
                <th>{L("Département", "Department")}</th>
                <th>Email</th>
                <th>{L("Mobile", "Mobile")}</th>
                <th>{L("Téléphone", "Phone")}</th>
                <th className="text-center">{t("contacts.dm")}</th>
                <th>{t("contacts.score")}</th>
                {editable && <th />}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => {
                const initials = `${(r.first_name || "?").charAt(0)}${(r.last_name || "").charAt(0)}`.toUpperCase();
                return (
                <tr
                  key={r.id}
                  onClick={(e) => {
                    const tag = (e.target.tagName || "").toLowerCase();
                    if (tag === "a" || tag === "button" || e.target.closest("a, button")) return;
                    nav(`/contacts/${r.id}`);
                  }}
                  className="cursor-pointer transition-colors group"
                  data-testid={`contact-row-${r.id}`}
                  style={{ borderBottom: "1px solid var(--border)" }}
                >
                  <td>
                    <div className="flex items-center gap-2.5">
                      <div
                        className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-[11px] font-semibold"
                        style={{
                          background: "var(--bg-subtle)",
                          color: "var(--text-secondary)",
                          border: "1px solid var(--border)",
                        }}
                      >
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-[13.5px] truncate flex items-center gap-1" style={{ color: "var(--text-primary)" }}>
                          {r.first_name} {r.last_name}
                          <ArrowRight size={10} className="opacity-0 group-hover:opacity-50 transition-opacity" />
                        </div>
                        {r.city && <div className="text-[11px] mt-0.5" style={{ color: "var(--text-tertiary)" }}>{r.city}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="text-[12.5px]" style={{ color: "var(--text-secondary)" }}>{r.title || "—"}</td>
                  <td className="text-[13px]"><OperatorLink name={r.operator} /></td>
                  <td className="text-[12px]" style={{ color: "var(--text-secondary)" }}>{r.department || "—"}</td>
                  <td className="font-mono text-[11.5px]">
                    {r.email ? (
                      <a href={`mailto:${r.email}`} className="hover:underline" style={{ color: "var(--text-primary)" }}>
                        {r.email}
                      </a>
                    ) : <span style={{ color: "var(--text-tertiary)" }}>—</span>}
                  </td>
                  <td className="font-mono text-[11.5px] whitespace-nowrap">
                    {r.mobile ? (
                      <a href={`tel:${String(r.mobile).replace(/\s+/g, "")}`} className="hover:underline" style={{ color: "var(--text-primary)" }}>
                        {r.mobile}
                      </a>
                    ) : <span style={{ color: "var(--text-tertiary)" }}>—</span>}
                  </td>
                  <td className="font-mono text-[11.5px] whitespace-nowrap">
                    {r.phone ? (
                      <a href={`tel:${String(r.phone).replace(/\s+/g, "")}`} className="hover:underline" style={{ color: "var(--text-primary)" }}>
                        {r.phone}
                      </a>
                    ) : <span style={{ color: "var(--text-tertiary)" }}>—</span>}
                  </td>
                  <td className="text-center">
                    {r.decision_maker ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider"
                            style={{ background: "rgba(237,32,39,0.08)", color: "var(--accent)", border: "1px solid rgba(237,32,39,0.30)" }}>
                        DM
                      </span>
                    ) : <span style={{ color: "var(--text-tertiary)" }}>—</span>}
                  </td>
                  <td>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star
                          key={i}
                          size={11}
                          weight={i <= (r.relationship_score || 0) ? "fill" : "regular"}
                          style={{ color: i <= (r.relationship_score || 0) ? "#F59E0B" : "var(--border)" }}
                        />
                      ))}
                    </div>
                  </td>
                  {editable && (
                    <td className="text-right whitespace-nowrap">
                      <button onClick={() => openEdit(r)} className="p-1 rounded hover:bg-[var(--bg-subtle)] transition-colors" style={{ color: "var(--text-tertiary)" }} data-testid={`edit-${r.id}`}><PencilSimple size={13} /></button>
                      <button onClick={() => del(r.id)} className="p-1 rounded hover:bg-[rgba(237,32,39,0.08)] transition-colors" style={{ color: "var(--text-tertiary)" }} data-testid={`delete-${r.id}`}><Trash size={13} /></button>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </Tile>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)" }} onClick={close}>
          <div className="tile p-7 w-full max-w-2xl fade-in" onClick={(e) => e.stopPropagation()} data-testid="contact-modal" style={{ boxShadow: "var(--shadow-lg)" }}>
            <h3 className="text-[20px] font-semibold mb-5 tracking-tight">{editing.id ? "Edit contact" : "New contact"}</h3>
            <div className="grid grid-cols-2 gap-3">
              <FieldText label="First Name" v={form.first_name} set={(v) => setForm({ ...form, first_name: v })} />
              <FieldText label="Last Name" v={form.last_name} set={(v) => setForm({ ...form, last_name: v })} />
              <FieldText label="Title" v={form.title} set={(v) => setForm({ ...form, title: v })} />
              <FieldText label="Department" v={form.department} set={(v) => setForm({ ...form, department: v })} />
              <FieldText label={t("table.operator")} v={form.operator} set={(v) => setForm({ ...form, operator: v })} />
              <FieldText label={t("table.city")} v={form.city} set={(v) => setForm({ ...form, city: v })} />
              <FieldText label="Email" v={form.email} set={(v) => setForm({ ...form, email: v })} />
              <FieldText label="Phone" v={form.phone} set={(v) => setForm({ ...form, phone: v })} />
              <FieldText label="Mobile" v={form.mobile} set={(v) => setForm({ ...form, mobile: v })} />
              <FieldText label="Language" v={form.language} set={(v) => setForm({ ...form, language: v })} />
              <FieldText label="Linked Platforms" v={form.linked_platforms} set={(v) => setForm({ ...form, linked_platforms: v })} colspan />
              <div className="col-span-2">
                <label className="label-tech block mb-1">Notes</label>
                <textarea className="input-tech" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="dm" checked={!!form.decision_maker} onChange={(e) => setForm({ ...form, decision_maker: e.target.checked })} className="accent-wabtec-red" />
                <label htmlFor="dm" className="text-sm">{t("contacts.dm")}</label>
              </div>
              <div>
                <label className="label-tech block mb-1">{t("contacts.score")} (1-5)</label>
                <input type="number" min={1} max={5} className="input-tech" value={form.relationship_score} onChange={(e) => setForm({ ...form, relationship_score: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={close} className="btn-ghost" data-testid="contact-cancel">{t("visits.cancel")}</button>
              <button onClick={save} className="btn-primary" data-testid="contact-save">{t("visits.save")}</button>
            </div>
          </div>
        </div>
      )}

      {importPreview && (
        <ImportPreviewModal
          preview={importPreview}
          onCancel={() => setImportPreview(null)}
          onConfirm={confirmImport}
          busy={importing}
          L={L}
        />
      )}
    </div>
  );
}

function ImportPreviewModal({ preview, onCancel, onConfirm, busy, L }) {
  const { summary, file } = preview;
  const total = summary.to_create + summary.to_update;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)" }}
      onClick={onCancel}
      data-testid="import-preview-modal"
    >
      <div className="tile p-7 w-full max-w-2xl fade-in" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "var(--shadow-lg)" }}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-[20px] font-semibold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
              {L("Aperçu de l'import", "Import preview")}
            </h3>
            <p className="text-[12px] mt-1 font-mono" style={{ color: "var(--text-tertiary)" }}>
              {file.name} · {(file.size / 1024).toFixed(1)} KB
            </p>
          </div>
          <button onClick={onCancel} className="p-1.5 rounded-full hover:bg-[var(--bg-subtle)]" data-testid="import-cancel-btn">
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-5">
          <StatCard label={L("À créer", "To create")} value={summary.to_create} color="#34A853" Icon={CheckCircle} />
          <StatCard label={L("À mettre à jour", "To update")} value={summary.to_update} color="#0D71DD" Icon={CheckCircle} />
          <StatCard
            label={L("Lignes ignorées", "Skipped")}
            value={summary.errors?.length || 0}
            color={summary.errors?.length ? "#F59E0B" : "var(--text-tertiary)"}
            Icon={WarningCircle}
          />
        </div>

        {summary.preview && summary.preview.length > 0 && (
          <div className="mb-4">
            <div className="label-tech mb-2">{L("Aperçu (10 premières lignes)", "Preview (first 10 rows)")}</div>
            <div className="overflow-x-auto rounded-[10px]" style={{ border: "1px solid var(--border)", maxHeight: 240 }}>
              <table className="tech w-full">
                <thead>
                  <tr>
                    <th>{L("Ligne", "Row")}</th>
                    <th>{L("Prénom", "First")}</th>
                    <th>{L("Nom", "Last")}</th>
                    <th>{L("Opérateur", "Operator")}</th>
                    <th>Email</th>
                    <th>Mobile</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.preview.map((p, i) => (
                    <tr key={i}>
                      <td className="font-mono text-[11px]">{p.row}</td>
                      <td>{p.first_name}</td>
                      <td>{p.last_name}</td>
                      <td className="text-[12px]">{p.operator}</td>
                      <td className="font-mono text-[11px]">{p.email || "—"}</td>
                      <td className="font-mono text-[11px]">{p.mobile || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {summary.errors && summary.errors.length > 0 && (
          <div className="mb-4 p-3 rounded-[10px]" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid #F59E0B" }}>
            <div className="text-[12px] font-medium mb-1.5" style={{ color: "#F59E0B" }}>
              {L("Lignes ignorées", "Skipped rows")} ({summary.errors.length})
            </div>
            <ul className="text-[11px] font-mono space-y-0.5 max-h-24 overflow-y-auto" style={{ color: "var(--text-tertiary)" }}>
              {summary.errors.slice(0, 10).map((e, i) => (
                <li key={i}>• ligne {e.row} — {e.reason}</li>
              ))}
              {summary.errors.length > 10 && <li>… +{summary.errors.length - 10}</li>}
            </ul>
          </div>
        )}

        <div className="flex items-center justify-between mt-2">
          <p className="text-[11px] font-mono" style={{ color: "var(--text-tertiary)" }}>
            {L(
              "Détection: doublons par email, sinon par opérateur + nom",
              "Match: by email, fallback to operator + name",
            )}
          </p>
          <div className="flex gap-2">
            <button onClick={onCancel} className="btn-ghost" data-testid="import-cancel-confirm-btn">
              {L("Annuler", "Cancel")}
            </button>
            <button
              onClick={onConfirm}
              disabled={busy || total === 0}
              className="btn-accent flex items-center gap-2"
              data-testid="import-confirm-btn"
            >
              <UploadSimple size={13} weight="bold" />
              {busy
                ? L("Import en cours…", "Importing…")
                : L(`Importer ${total} contact(s)`, `Import ${total} contact(s)`)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, Icon }) {
  return (
    <div className="p-3 rounded-[10px]" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={12} weight="fill" style={{ color }} />
        <span className="label-tech">{label}</span>
      </div>
      <div className="text-[22px] font-semibold font-mono leading-none" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function FieldText({ label, v, set, colspan }) {
  return (
    <div className={colspan ? "col-span-2" : ""}>
      <label className="label-tech block mb-1">{label}</label>
      <input className="input-tech" value={v || ""} onChange={(e) => set(e.target.value)} />
    </div>
  );
}
